/**
 * MarketingSchedule — calendar/list view of upcoming and past auto-
 * publishes. Two panels:
 *   1) Upcoming — every article with status='scheduled', earliest first.
 *      Admin can jump into the editor with one click.
 *   2) History — every social post attempt with real status, LinkedIn
 *      post link/URN, error and retry (legacy MOCKED rows keep a pill).
 *
 * A full month-grid calendar can land in a later iteration; the list
 * view is what the admin actually needs day-to-day.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarClock, ExternalLink, RefreshCw, Loader2, Linkedin, Facebook, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { getMarketingSchedule, retrySocialPost, linkedinStatus } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { formatDate } from '../../utils/date';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${formatDate(d)} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const platformIcon = (platform) => {
  const p = (platform || '').toLowerCase();
  if (p === 'linkedin') return <Linkedin className="h-4 w-4 text-blue-700" />;
  if (p === 'facebook') return <Facebook className="h-4 w-4 text-blue-600" />;
  return <FileText className="h-4 w-4 text-slate-500" />;
};

const STATUS_STYLES = {
  sent: ['Sent', 'bg-emerald-50 border-emerald-200 text-emerald-800'],
  failed: ['Failed', 'bg-red-50 border-red-200 text-red-800'],
  unknown: ['Outcome unknown', 'bg-orange-50 border-orange-200 text-orange-800'],
  posting: ['Posting…', 'bg-blue-50 border-blue-200 text-blue-800'],
  blocked: ['Blocked — carousel not ready', 'bg-red-50 border-red-200 text-red-800'],
};

const StatusCell = ({ r, onRetry, busy }) => {
  if (r.mocked) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium" data-testid={`social-status-${r.id}`}>
        MOCKED
      </span>
    );
  }
  const [label, cls] = STATUS_STYLES[r.status] || [r.status, 'bg-slate-50 border-slate-200 text-slate-700'];
  const canRetry = r.platform === 'linkedin' && ['failed', 'unknown', 'posting', 'blocked'].includes(r.status);
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`} data-testid={`social-status-${r.id}`}>
          {label}
        </span>
        {r.externalUrl && (
          <a href={r.externalUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`social-link-${r.id}`}>
            View post <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {canRetry && (
          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy} onClick={() => onRetry(r)} data-testid={`social-retry-${r.id}`}>
            Retry
          </Button>
        )}
      </div>
      {r.postUrn && <div className="text-[10px] font-mono text-slate-500" data-testid={`social-urn-${r.id}`}>{r.postUrn}</div>}
      {r.error && (
        <div className="text-[11px] text-red-700 break-words max-w-md line-clamp-3" title={r.error} data-testid={`social-error-${r.id}`}>
          {r.failedStage ? `[${r.failedStage}] ` : ''}{r.error}
        </div>
      )}
      {r.attemptCount > 1 && <div className="text-[10px] text-slate-500">{r.attemptCount} attempts</div>}
    </div>
  );
};

const MarketingSchedule = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ scheduled: [], social: [] });
  const [loading, setLoading] = useState(true);
  const [liState, setLiState] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const retry = async (r) => {
    let confirmNotPosted = false;
    if (!['failed', 'blocked'].includes(r.status)) {
      confirmNotPosted = window.confirm(
        'The outcome of this post is unknown. Open the Lutrom Engineering Company Page and check it was NOT posted.\n\nClick OK only if it is not on LinkedIn.',
      );
      if (!confirmNotPosted) return;
    }
    setRetrying(r.id);
    try {
      const out = await retrySocialPost(r.id, confirmNotPosted);
      if (out.status === 'sent') toast.success('Posted to LinkedIn');
      else toast.error(out.error || `Retry finished with status ${out.status}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const reload = () => {
    setLoading(true);
    return getMarketingSchedule()
      .then(setData)
      .catch(() => toast.error('Could not load schedule'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    linkedinStatus().then(setLiState).catch(() => setLiState({ ready: false, notReadyReason: 'Could not load LinkedIn status.' }));
  }, []);

  return (
    <div className="space-y-6" data-testid="marketing-schedule-page">
      <PageHeader
        icon={CalendarClock}
        title="Marketing — Schedule"
        subtitle="Upcoming auto-publishes + social auto-post history"
        actions={
          <Button
            variant="outline"
            onClick={reload}
            disabled={loading}
            data-testid="schedule-reload"
          >
            {loading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…</>
              : <><RefreshCw className="h-4 w-4 mr-2" /> Refresh</>}
          </Button>
        }
      />

      <div
        className={`rounded-md border px-4 py-3 text-sm flex flex-wrap items-center gap-2 ${liState?.ready ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}
        data-testid="schedule-linkedin-connection"
      >
        <Linkedin className="h-4 w-4" />
        {!liState ? 'Checking LinkedIn connection…'
          : liState.ready ? <>LinkedIn connected — auto-posts go live to <span className="font-mono text-xs">{liState.orgUrn}</span></>
          : <>LinkedIn not ready: {liState.notReadyReason}</>}
        <span className="text-xs text-slate-600 ml-auto">Facebook auto-posting disabled (integration mocked)</span>
      </div>

      {/* Upcoming */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
            Upcoming ({data.scheduled.length})
          </div>
        </div>
        {data.scheduled.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Nothing scheduled. Open an article and hit <span className="font-medium">Schedule</span>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Article</th>
                  <th className="px-4 py-2 hidden md:table-cell">Auto-post to</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.scheduled.map((r) => (
                  <tr key={r.id} data-testid={`schedule-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-slate-900 text-xs whitespace-nowrap">
                      {formatDateTime(r.scheduledFor)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 truncate max-w-[12rem] sm:max-w-md">
                        {r.title}
                      </div>
                      <div className="text-xs text-slate-500 font-mono truncate max-w-[12rem] sm:max-w-md">
                        /insights/{r.slug}
                      </div>
                      {/* Platforms shown inline on mobile since the column is hidden */}
                      <div className="flex flex-wrap items-center gap-1 mt-1 md:hidden">
                        {r.socialTargets?.linkedin && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[10px]">
                            <Linkedin className="h-3 w-3" /> LinkedIn
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        {r.socialTargets?.linkedin && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px]">
                            <Linkedin className="h-3 w-3" /> LinkedIn
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/marketing/articles/${r.id}`)}
                        data-testid={`open-${r.id}`}
                      >
                        <ExternalLink className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Open</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
            Social post history ({data.social.length})
          </div>
        </div>
        {data.social.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No social posts yet — schedule an article to see history here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-2 hidden sm:table-cell">Sent</th>
                  <th className="px-4 py-2">Platform</th>
                  <th className="px-4 py-2">Article</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.social.map((r) => (
                  <tr key={r.id} data-testid={`social-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-slate-700 text-xs whitespace-nowrap hidden sm:table-cell">
                      {formatDateTime(r.sentAt || r.startedAt || r.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {platformIcon(r.platform)}
                        <span className="capitalize">{r.platform}</span>
                      </div>
                      {/* Timestamp inline on mobile since the column is hidden */}
                      <div className="text-[10px] text-slate-500 mt-0.5 sm:hidden">
                        {formatDateTime(r.sentAt || r.startedAt || r.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 truncate max-w-[10rem] sm:max-w-md">
                      <span className="font-mono text-xs">{r.articleSlug}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusCell r={r} onRetry={retry} busy={retrying === r.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingSchedule;
